import { dailyTaskTemplateService } from './dailyTaskTemplateService';
import {
  DailyTaskActionType,
  DailyTaskCategory,
  DailyTaskColorVariant,
  DailyTaskDeepLinkTarget,
  DayKind,
} from './dailyTaskTemplateTypes';
import type { DailyTaskTemplateFirestore } from './dailyTaskTemplateTypes';

export interface ImportResult {
  total: number;
  created: number;
  updated: number;
}

const COLOR_VARIANTS = Object.values(DailyTaskColorVariant) as string[];
const CATEGORIES = Object.values(DailyTaskCategory) as string[];
const ACTION_TYPES = Object.values(DailyTaskActionType) as string[];
const DEEP_LINK_TARGETS = Object.values(DailyTaskDeepLinkTarget) as string[];
const DAY_KINDS = Object.values(DayKind) as string[];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((v) => typeof v === 'string');

const isNumberArray = (value: unknown): value is number[] =>
  Array.isArray(value) && value.every((v) => typeof v === 'number');

/** Returns the first validation problem, or null if the item is a valid template. */
const validateTemplate = (item: unknown): string | null => {
  if (!isRecord(item)) return 'expected an object';

  if (typeof item.title !== 'string') return 'title must be a string';
  if (typeof item.emoji !== 'string') return 'emoji must be a string';
  if (typeof item.textTemplatingPrompt !== 'string') return 'textTemplatingPrompt must be a string';
  if (typeof item.isPublished !== 'boolean') return 'isPublished must be a boolean';
  if (typeof item.totalSteps !== 'number') return 'totalSteps must be a number';
  if (typeof item.energy !== 'number') return 'energy must be a number';

  if (typeof item.colorVariant !== 'string' || !COLOR_VARIANTS.includes(item.colorVariant)) {
    return `colorVariant must be one of: ${COLOR_VARIANTS.join(', ')}`;
  }
  if (typeof item.category !== 'string' || !CATEGORIES.includes(item.category)) {
    return `category must be one of: ${CATEGORIES.join(', ')}`;
  }
  if (typeof item.actionType !== 'string' || !ACTION_TYPES.includes(item.actionType)) {
    return `actionType must be one of: ${ACTION_TYPES.join(', ')}`;
  }

  if (!isStringArray(item.ocdSubtypeTags)) return 'ocdSubtypeTags must be an array of strings';
  if (!isNumberArray(item.journeyChapterRelevance)) {
    return 'journeyChapterRelevance must be an array of numbers';
  }
  if (
    !Array.isArray(item.dayKindTags) ||
    !item.dayKindTags.every((v) => typeof v === 'string' && DAY_KINDS.includes(v))
  ) {
    return `dayKindTags must be an array of: ${DAY_KINDS.join(', ')}`;
  }

  if (item.deepLinkTarget !== undefined) {
    if (typeof item.deepLinkTarget !== 'string' || !DEEP_LINK_TARGETS.includes(item.deepLinkTarget)) {
      return `deepLinkTarget must be one of: ${DEEP_LINK_TARGETS.join(', ')}`;
    }
  }
  if (item.deepLinkParams !== undefined) {
    if (
      !isRecord(item.deepLinkParams) ||
      !Object.values(item.deepLinkParams).every((v) => typeof v === 'string')
    ) {
      return 'deepLinkParams must be an object of string values';
    }
  }

  return null;
};

/**
 * Validates and imports an array of DailyTaskTemplateFirestore objects.
 * All-or-nothing: if any item is malformed, nothing is written.
 */
export const importDailyTaskTemplates = async (raw: unknown): Promise<ImportResult> => {
  if (!Array.isArray(raw)) {
    throw new Error('Expected a JSON array of daily task templates.');
  }

  const errors: string[] = [];
  raw.forEach((item, i) => {
    const problem = validateTemplate(item);
    if (problem) errors.push(`Item ${i}: ${problem}`);
  });
  if (errors.length > 0) {
    throw new Error(`Import aborted — no templates were written.\n\n${errors.join('\n')}`);
  }

  const templates = raw as DailyTaskTemplateFirestore[];

  const existing = await dailyTaskTemplateService.getAll();
  const existingIds = new Set(existing.map((t) => t.id));
  let created = 0;
  let updated = 0;
  for (const template of templates) {
    if (template.id && existingIds.has(template.id)) {
      updated++;
    } else {
      created++;
    }
  }

  await dailyTaskTemplateService.importTemplates(templates);

  return { total: templates.length, created, updated };
};
